import { FIELDS } from 'modules/cm/fields/types';
import { dateValidation } from 'utils/date';
import {
  nullableNumber,
  nullableString,
  positiveCurrencyIncludingZeroValidation,
  sortCodeValidation,
} from 'utils/form/validation';
import * as yup from 'yup';
import { FormModel } from '../../../pages/CaseDetailsPage/subPages/Summary/Summary.types';
import { AffordabilityResults } from 'modules/cm/domain/Case/LoanDetails';
import { ProductRepaymentTypes, ProductType, ProductTypes } from 'domain/Product';

const messages = {
  required: 'This field is required',
  update: 'Please update',
};

export default yup.object().shape<FormModel>({
  [FIELDS.ID]: nullableNumber().required(messages.required),
  [FIELDS.APPLICATION_TYPE]: nullableString().required(messages.required),
  [FIELDS.DELPHI_SCORE]: nullableNumber().required(messages.required),
  [FIELDS.SOURCE_OF_DEPOSIT]: nullableString().required(messages.required),
  [FIELDS.IS_PRIVATE_SALE]: yup
    .boolean()
    .nullable()
    .required(messages.required),
  [FIELDS.IS_FIRST_TIME_BUYER]: yup
    .boolean()
    .nullable()
    .required(messages.required),
  [FIELDS.IS_BUY_TO_LET]: yup
    .boolean()
    .nullable()
    .required(messages.required),
  [FIELDS.PURPOSE_OF_CAPITAL_RAISE]: nullableString(),
  [FIELDS.CREDIT_POLICY_EXEMPTION_RATIONALE]: nullableString().when(FIELDS.IS_CREDIT_POLICY_EXEMPTION, {
    is: 'true',
    then: yup.string().required(messages.required),
  }),
  [FIELDS.HAS_ADVERSE_CREDIT]: nullableString(),
  [FIELDS.IS_CREDIT_POLICY_EXEMPTION]: nullableString(),
  [FIELDS.OWNER]: nullableString(),
  [FIELDS.STATUS]: nullableString(),
  [FIELDS.SORT_CODE]: sortCodeValidation()
    .nullable()
    .required(messages.required),
  [FIELDS.ACCOUNT_NAME]: nullableString().required(messages.required),
  [FIELDS.BANK_OR_BUILDING_SOCIETY_NAME]: nullableString().required(messages.required),
  [FIELDS.PURPOSE_OF_THE_LOAN]: nullableString(),
  [FIELDS.PEPPER_PURPOSE_OF_THE_LOAN]: nullableString(),
  [FIELDS.LOAN_AMOUNT_PURCHASE]: nullableNumber(),
  [FIELDS.LOAN_AMOUNT_REMORTGAGE]: nullableNumber(),
  [FIELDS.LOAN_HOME_IMPROVEMENTS]: nullableNumber(),
  [FIELDS.LOAN_DISCRETIONARY_SPEND]: nullableNumber(),
  [FIELDS.LOAN_ASSISTING_OTHERS]: nullableNumber(),
  [FIELDS.LOAN_OTHER_PURPOSES]: nullableNumber(),
  [FIELDS.NET_LOAN]: nullableNumber().required(messages.required),
  [FIELDS.ESTIMATED_PROPERTY_VALUE]: nullableNumber(),
  [FIELDS.PROPERTY_PURCHASE_PRICE]: nullableNumber(),
  [FIELDS.ACTUAL_PROPERTY_VALUE]: nullableNumber(),
  [FIELDS.CURRENT_MORTGAGE_PROVIDER]: nullableString(),
  [FIELDS.VALUATION_FEE_PAYMENT]: nullableString(),
  [FIELDS.RE_INSPECTION_FEE]: nullableNumber(),
  [FIELDS.POSTCODE]: nullableString().required(messages.required),
  [FIELDS.PRODUCT_CODE]: nullableString().required(messages.update),
  [FIELDS.PRODUCT_TIER]: nullableString().required(messages.required),
  [FIELDS.PRODUCT_FEE_PAYMENT_METHOD]: nullableString(),
  [FIELDS.PRODUCT_REPAYMENT_TYPE]: yup
    .string<ProductRepaymentTypes>()
    .nullable()
    .when(FIELDS.PRODUCT_TYPE, {
      is: (val: any) => val && val !== ProductTypes.RIO,
      then: yup
        .string<ProductRepaymentTypes>()
        .nullable()
        .required(messages.required),
      else: yup.string<ProductRepaymentTypes>().nullable(),
    }),
  [FIELDS.CREATED_BY]: nullableNumber().required(messages.required),
  [FIELDS.ACCOUNT_NUMBER]: nullableString().required(messages.required),
  [FIELDS.LOAN_DEBT_CONSOLIDATION]: nullableNumber(),
  [FIELDS.MONTHLY_BASIC_SPEND]: nullableNumber().required(messages.required),
  [FIELDS.STAGE]: nullableString().required(messages.required),
  [FIELDS.UPDATED_AT]: nullableString().required(messages.required),
  [FIELDS.DEPOSIT_SOURCE_BUILDERS]: yup.boolean().required(messages.required),
  [FIELDS.DEPOSIT_SOURCE_DETAILS]: nullableString().when(FIELDS.DEPOSIT_SOURCE_OTHER, {
    is: true,
    then: yup.string().required(messages.required),
  }),
  [FIELDS.DEPOSIT_SOURCE_EQUITY_FROM_SALE]: yup.boolean().required(messages.required),
  [FIELDS.DEPOSIT_SOURCE_GIFTS]: yup.boolean().required(messages.required),
  [FIELDS.DEPOSIT_SOURCE_OTHER]: yup.boolean().required(messages.required),
  [FIELDS.DEPOSIT_SOURCE_SAVINGS]: yup.boolean().required(messages.required),
  [FIELDS.APPLICATION_TYPE]: nullableString(),
  [FIELDS.HAS_MARKETING_EMAIL]: yup.boolean().nullable(),
  [FIELDS.HAS_MARKETING_PHONE]: yup.boolean().nullable(),
  [FIELDS.HAS_MARKETING_POST]: yup.boolean().nullable(),
  [FIELDS.HAS_MARKETING_SMS]: yup.boolean().nullable(),
  [FIELDS.FCA_LOAN_PURPOSE]: nullableString(),
  [FIELDS.OUTSTANDING_MORTGAGE_AMOUNT]: nullableNumber(),
  [FIELDS.SOURCE_OF_FUNDS_TO_CLEAR_OUTSTANDING_MORTGAGE]: nullableString(),
  [FIELDS.EXISTING_MORTGAGE_STATUS]: nullableString(),
  [FIELDS.ADDITIONAL_DETAILS]: nullableString(),
  [FIELDS.IS_PEPPER_UPLOAD_APPROVED]: yup.boolean().nullable(),
  [FIELDS.DELEGATED_AUTHORITY_LEVEL]: nullableString(),
  [FIELDS.INITIAL_ASSESSMENT_BY]: nullableString(),
  [FIELDS.INITIAL_ASSESSMENT_DATE]: dateValidation().nullable(),
  [FIELDS.INITIAL_ASSESSMENT_APPROVED_BY]: nullableString(),
  [FIELDS.INITIAL_ASSESSMENT_APPROVED_DATE]: dateValidation().nullable(),
  [FIELDS.UW_RECOMMENDATION]: nullableString(),
  [FIELDS.UW_SPECIAL_CONDITIONS]: nullableString(),
  [FIELDS.AFT_PREFIX]: yup.object().shape<AffordabilityResults>({
    [FIELDS.AFT_STAGE]: nullableString(),
    [FIELDS.AFT_INCOME]: positiveCurrencyIncludingZeroValidation(),
    [FIELDS.AFT_BUFFER]: positiveCurrencyIncludingZeroValidation(),
    [FIELDS.AFT_LIVING_COSTS]: positiveCurrencyIncludingZeroValidation(),
    [FIELDS.AFT_NET_CASH_FLOW]: positiveCurrencyIncludingZeroValidation(),
    [FIELDS.AFT_MAX_LOAN_POSSIBLE]: positiveCurrencyIncludingZeroValidation(),
  }),
  [FIELDS.TERM_LENGTH]: nullableNumber(),
  [FIELDS.PRODUCT_TYPE]: yup.string<ProductType>().nullable(),
  [FIELDS.IS_ON_HOLD_STATUS]: yup.boolean().nullable(),
  [FIELDS.IS_CALL_CARRIED_ALL_APPLICANTS]: yup.boolean().nullable(),
  [FIELDS.IS_APPLICATION_APPROVED_AUTHORITY_LEVELS]: yup.boolean().nullable(),
});
